import { type ReactNode, useState } from "react";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { useTranslate, useUpdate } from "ra-core";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DateField } from "@/components/admin/date-field";
import type { Task } from "../types";
import { DueCountdown } from "./DueCountdown";
import { dueDateInDays } from "./taskUtils";

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

// Parse a stored value as a local date (date-only strings must not shift a day).
const toDate = (value?: string | null) =>
  value
    ? new Date(value.length <= 10 ? `${value}T00:00:00` : value)
    : undefined;

const useSetDue = (task: Task) => {
  const [update] = useUpdate();
  return (value: string | null) =>
    update(
      "tasks",
      { id: task.id, data: { due_date: value }, previousData: task },
      { mutationMode: "optimistic" },
    );
};

const triggerClass =
  "rounded px-1 -mx-0.5 align-middle hover:bg-accent cursor-pointer";

// The date → a calendar to pick any specific date.
function DatePill({ task, children }: { task: Task; children: ReactNode }) {
  const setDue = useSetDue(task);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={stop}>
        <button type="button" className={triggerClass}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" onClick={stop}>
        <Calendar
          mode="single"
          selected={toDate(task.due_date)}
          defaultMonth={toDate(task.due_date)}
          onSelect={(day) => {
            if (day) {
              setDue(format(day, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

const Preset = ({
  onSelect,
  children,
}: {
  onSelect: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className="text-left px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
  >
    {children}
  </button>
);

// The countdown → quick reschedule presets.
function CountdownPill({ task }: { task: Task }) {
  const translate = useTranslate();
  const setDue = useSetDue(task);
  const [open, setOpen] = useState(false);
  const pick = (value: string | null) => {
    setDue(value);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={stop}>
        <button type="button" className={triggerClass}>
          <DueCountdown date={task.due_date!} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 p-1" onClick={stop}>
        <div className="flex flex-col text-sm">
          <Preset onSelect={() => pick(dueDateInDays(0))}>
            {translate("resources.tasks.actions.today", { _: "Today" })}
          </Preset>
          <Preset onSelect={() => pick(dueDateInDays(1))}>
            {translate("resources.tasks.actions.tomorrow", { _: "Tomorrow" })}
          </Preset>
          <Preset onSelect={() => pick(dueDateInDays(7))}>
            {translate("resources.tasks.actions.next_week", { _: "Next week" })}
          </Preset>
          <Preset onSelect={() => pick(null)}>
            {translate("resources.tasks.actions.clear_due", {
              _: "Clear due date",
            })}
          </Preset>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Due date shown as two click-to-edit pills: the date opens a calendar, the
// countdown opens quick reschedule presets.
export function DueDatePill({ task }: { task: Task }) {
  const translate = useTranslate();
  if (!task.due_date) {
    return (
      <DatePill task={task}>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <CalendarDays className="w-3 h-3" />
          {translate("resources.tasks.actions.set_due_date", {
            _: "Set due date",
          })}
        </span>
      </DatePill>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <DatePill task={task}>
        <DateField source="due_date" record={task} showDate showTime />
      </DatePill>
      <span className="text-muted-foreground">·</span>
      <CountdownPill task={task} />
    </span>
  );
}
